import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Mail, ChevronRight, Download, X } from 'lucide-react'
import { Spinner } from '../components/UI'
import { FilmReel } from '../components/FilmReelIcon'
import { tmdbGet } from '../lib/tmdb'
import { supabase } from '../lib/supabase'
import { safeStorage } from '../lib/safeStorage'
import { tvtimeGone } from '../lib/tvtime'
import { logEvent } from '../lib/analytics'
import { isPlayInstall, LANDING_KEY } from '../lib/playPrompt'
import { friendlyAuthError } from '../lib/authError'
import { suggestEmailFix } from '../lib/emailTypo'
import { NextEpLogo } from '../components/NextEpLogo'
import { Wordmark } from '../components/Wordmark'
import { Button } from '../components/UI'

// ── GuestHome — shown on / when no user is authenticated ─────────────────────
// This IS the marketing site — NextEp has no separate landing domain, this
// screen carries the entire "why should I sign up" pitch. Rebuilt 2026-07-03
// (Jason: "design the entire website advertising the app") from a single flat
// card stack into: cinematic hero → signature-feature showcases with real mini
// mockups (not just an emoji + a sentence) → sign-in → footer. Reuses the
// ticket motif from WelcomeWalkthrough (already proven) and the app's existing
// motion vocabulary — no new easing curves or colors introduced.
//
// Extracted to its own EAGER module (2026-07-06 perf audit): App routes logged-out
// visitors straight here, so the heavy authenticated Home tree (now a lazy chunk)
// is never downloaded on the marketing landing — the coldest, most bundle-sensitive,
// conversion-critical load. Keep this file's imports LIGHT.import { FilmReel } from '../components/FilmReelIcon'
import { ClapboardClosed } from '../components/ClapboardIcon'

export default function GuestHome() {
  const [email,    setEmail]   = useState('')
  const [loading,  setLoading] = useState(false)
  const [sent,     setSent]    = useState(false)
  const [resendIn, setResendIn] = useState(0)   // magic-link resend cooldown (s)
  const [waitlisted, setWaitlisted] = useState(false)
  const [err,      setErr]     = useState('')
  const [linkErr,  setLinkErr] = useState('')   // "that link expired" banner
  const [code, setCode] = useState('')
  const [codeLoading, setCodeLoading] = useState(false)
  const signInRef = useRef(null)
  const navigate  = useNavigate()

  // Installed-PWA leak: a magic link opens in Safari, whose session never reaches the
  // standalone app — the user taps the link, authenticates in the browser, and returns
  // to a still-logged-out PWA (dead end). When we're running standalone, LEAD with the
  // password path (which authenticates fully in-app) instead of demoting it to a footer
  // link. In the browser, magic-link stays the lead (nothing to invent). (audit 2026-07-20)
  const isStandalone = (() => {
    try { return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator?.standalone || false }
    catch { return false }
  })()

  // Coarse-pointer (touch) devices get a jumpy viewport + surprise keyboard from
  // autoFocus — the email field here sits below a full hero, so on a phone
  // autoFocus would both pop the keyboard AND yank the page down away from the
  // pitch nobody's read yet. Desktop mouse users don't get a keyboard pop, so it
  // stays safe there. Matches the same guard added to Login.jsx.
  const skipAutoFocus = (() => {
    try { return window.matchMedia?.('(pointer: coarse)').matches } catch { return false }
  })()

  // Migration referral: the marketing site's "import from TV Time" CTAs land here
  // with ?from=tvtime. We (a) render a rescue-framed hero that speaks to the refugee's
  // actual job (get my history out before July 15), and (b) persist the intent across
  // the magic-link round-trip so the importer opens itself once they're in.
  const fromTvTime = (() => {
    try { return new URLSearchParams(window.location.search).get('from') === 'tvtime' }
    catch { return false }
  })()
  // Referral tag (?ref=finish, ?ref=tvtime-alt, …) from shared cards / links. Captured
  // so inbound viral traffic is MEASURABLE — stashed so the eventual signup event can
  // report where the user actually came from, closing the loop we couldn't see before.
  const refTag = (() => {
    try { return new URLSearchParams(window.location.search).get('ref') || null }
    catch { return null }
  })()
  useEffect(() => {
    if (fromTvTime) safeStorage.set('nextep_import_intent', '1')
    if (refTag) safeStorage.set('nextep_ref', refTag)
    // Paid arrivals skip the pitch (2026-10-14): they just clicked an ad that made it.
    // Straight into "Add a show or two"; the landing stays for everyone else.
    if (refTag === 'ads-search' || refTag === 'ads') { logEvent('landing_skip', { ref: refTag }); navigate('/start?step=addshow&ref=' + refTag, { replace: true }); return }
    // The switcher keywords ("tv time alternative", "trakt alternative") land on the same
    // step in SWITCH intent: import first, search second (2026-10-16).
    if (refTag === 'ads-switch') { logEvent('landing_skip', { ref: refTag, intent: 'switch' }); navigate('/start?step=addshow&intent=switch&ref=' + refTag, { replace: true }); return }
    // Only pass `ref` when we actually HAVE one. `ref: undefined` is still an own
    // property, so spreading it into logEvent's props overwrote the first-touch
    // attribution logEvent had already merged in — and JSON.stringify then dropped the
    // key entirely. Net effect: every untagged landing view recorded ref = null, and
    // REFERRER-based attribution (the discord.com / reddit.com case, where there's no
    // ?ref= in the URL at all) was destroyed at the one event that matters most for
    // measuring a new channel. 1,530 landing views lost this way before it was caught
    // (2026-08-01). getAttribution() already handles the no-tag case correctly.
    logEvent('landing_view', {
      from: fromTvTime ? 'tvtime' : 'direct',
      ...(refTag ? { ref: refTag } : {}),
    })
  }, [fromTvTime, refTag])

  // Play Store first launch → /start (17.029). 17.027 put this decision in HomeDeck,
  // but a fresh Play install has no session yet, so it lands HERE and HomeDeck never
  // mounts: 0 landing_variant events from 4 signed-out Play boots on 2026-09-23/24,
  // two of which wandered Home → Discover → Login and left. Same key, same once-per-
  // device rule, so a returning visitor is never pushed twice. A bare "/" only: a
  // magic-link return or ?from=/?ref= landing keeps its own job.
  useEffect(() => {
    if (window.location.search || window.location.hash) return
    if (!isPlayInstall()) return
    let seen = null
    try { seen = safeStorage.get(LANDING_KEY) } catch {}
    if (seen) return
    try { safeStorage.set(LANDING_KEY, 'play-start') } catch {}
    logEvent('landing_variant', { variant: 'play-start', signed_out: true })
    navigate('/start?from=play', { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Expired / already-used magic link: Supabase redirects back with an error in the
  // hash or query (e.g. otp_expired). Previously nothing read it, so the user silently
  // landed logged-out with no explanation. Detect it, show a friendly banner, and clean
  // the URL so a refresh doesn't re-show it.
  useEffect(() => {
    try {
      const h = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const q = new URLSearchParams(window.location.search)
      const err = h.get('error_description') || q.get('error_description') || h.get('error') || q.get('error')
      if (err) {
        setLinkErr('That sign-in link expired or was already used. Enter your email for a fresh one.')
        history.replaceState(null, '', window.location.pathname)
      }
    } catch {}
  }, [])

  // Resend cooldown tick (see Login — throttle-aware resend instead of a dead-end).
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  // Real, live content instead of emoji mockups: pull this week's trending shows
  // (public TMDB, no auth) so a logged-out visitor sees the actual catalog — proof
  // the product is real before the email gate. Tapping through goes to /discover,
  // which works logged-out (adding a show there prompts sign-in).
  const [peek, setPeek] = useState([])
  useEffect(() => {
    let cancel = false
    tmdbGet('/trending/tv/week')
      .then(d => { if (!cancel) setPeek((d.results || []).filter(s => s.poster_path).slice(0, 12)) })
      .catch(() => {})
    return () => { cancel = true }
  }, [])

  const sendMagicLink = async () => {
    if (!email.trim() || loading) return
    setLoading(true); setErr('')
    const addr = email.trim().toLowerCase()
    // Capacity gate parity with /login: at the user cap, only EXISTING accounts sign
    // in (shouldCreateUser:false); a new email then errors → we reserve their waitlist
    // spot instead of a broken signup. Fail open on a transient status hiccup so
    // sign-in never breaks. (Without this, GuestHome would leak past the cap that
    // /login enforces.)
    let open = true
    try { const { data } = await supabase.rpc('signup_status'); if (data && data.open === false) open = false } catch {}
    const { error } = await supabase.auth.signInWithOtp({
      email: addr,
      // Carry the TV Time intent THROUGH the link, not just via localStorage — a refugee
      // who enters their email on desktop but opens the link on their phone would otherwise
      // land with no importer (the flag lives in the other browser). Home reads ?from=tvtime.
      options: { emailRedirectTo: window.location.origin + (fromTvTime ? '/?from=tvtime' : '/'), shouldCreateUser: open }
    })
    setLoading(false)
    if (error) {
      // A transient send error (per-email OTP throttle, "wait 60 seconds") is NOT a
      // capacity signal — don't falsely waitlist a returning member for it; show a
      // human retry message instead of the raw Supabase string.
      const transient = /rate limit|60 seconds|too many|temporarily/i.test(error.message || '')
      if (!open && !transient) {
        try { await supabase.rpc('join_waitlist', { p_email: addr }) } catch {}
        logEvent('waitlist_join', { from: fromTvTime ? 'tvtime' : 'direct', ref: refTag || undefined })
        setWaitlisted(true); return
      }
      setErr(transient ? 'Too many attempts just now, give it a minute, then try again.' : friendlyAuthError(error))
      return
    }
    logEvent('signup_link_sent', { from: fromTvTime ? 'tvtime' : 'direct', ref: refTag || undefined })
    setSent(true); setResendIn(60)
  }

  // ── Code sign-in ────────────────────────────────────────────────────────────
  // The emailed LINK is not a reliable delivery mechanism: mail-provider link
  // scanners fetch {{ .ConfirmationURL }} before the human ever opens the message,
  // which burns the one-time token and leaves the real person on "link expired or
  // already used". Measured 2026-08-30: every ghost session had a lifetime of
  // exactly 0 seconds, ~40% of signups. A typed code can't be prefetched, so the
  // email now leads with one and this is the path that actually completes.
  const verifyCode = async () => {
    const token = code.replace(/\D/g, '')
    if (token.length < 6 || codeLoading) return
    setCodeLoading(true); setErr('')
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(), token, type: 'email',
    })
    setCodeLoading(false)
    if (error) {
      const expired = /expired|invalid|token/i.test(error.message || '')
      // Logged so we can tell "nobody is using codes" apart from "codes are failing" —
      // the difference between the email template not being pasted and a real bug.
      logEvent('code_signin_fail', { reason: expired ? 'expired_or_invalid' : 'other' })
      setErr(expired
        ? 'That code is wrong or has expired. Check the latest email, or resend below.'
        : friendlyAuthError(error))
      return
    }
    logEvent('code_signin_ok')
    // onAuthStateChange in useAuth takes it from here; no navigate needed.
  }

  const scrollToSignIn = () => signInRef.current?.scrollIntoView({ behavior:'smooth', block:'center' })

  // Derived, not state: recomputing on each keystroke is cheap and avoids an
  // effect that could lag a fast typer by a render.
  const emailFix = suggestEmailFix(email)

  const FEATURES = [
    { icon: '🎬', text: 'One card on Home: the next episode you should watch, across every show' },
    { icon: '👆', text: 'Swipe left to mark it watched. Tap it to rate. That’s the app' },
    { icon: '⭕', text: 'On a show, tap a ring to mark an episode, or drag down to log a whole binge' },
    { icon: '🔔', text: 'One alert when a show you follow airs, in your timezone. Nothing else unless you ask' },
    { icon: '📥', text: 'Import your history from TV Time, Trakt, Letterboxd & more, in one tap' },
    { icon: '🎛️', text: 'Friends, games and discussion rooms are switches. Off until you want them' },
  ]

  return (
    <div className='surface-wide gh-page' style={{ minHeight:'100dvh', background:'var(--bg)', fontFamily:'var(--font)', display:'flex', flexDirection:'column', overflow:'hidden' }}>

      {/* Broadsheet masthead — desktop only (hidden <720px via .gh-masthead). */}
      <div className='gh-masthead'>
        <span>Est. MMXXVI</span>
        <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}>◆ <Wordmark size={11} /> ◆</span>
        <span>Never Miss an Episode</span>
      </div>

      {/* ── HERO, film-grain vignette + glowing wordmark ─────────────────── */}
      <div style={{
        position:'relative', overflow:'hidden',
        padding:'max(56px, calc(env(safe-area-inset-top) + 20px)) 24px 34px',
        textAlign:'center',
        backgroundImage:`
          repeating-linear-gradient(125deg, rgba(var(--amber-rgb),0.035) 0 1px, transparent 1px 8px),
          radial-gradient(ellipse 120% 70% at 50% 0%, rgba(var(--amber-rgb),0.14) 0%, rgba(var(--amber-rgb),0) 70%)`,
      }}>
        {/* Marquee bulb strip (design sync 2026-08-15) — purely decorative theater
            framing above the logo, matches the "Now Showing" marquee concept. */}
        <div aria-hidden style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:16 }}>
          {Array.from({ length: 9 }).map((_, i) => (
            <span key={i} className="gh-bulb" style={{ width:6, height:6, borderRadius:'50%', background:'var(--amber)', boxShadow:'0 0 6px var(--amber)', animationDelay:`${i * 0.15}s` }} />
          ))}
        </div>
        <div style={{ display:'flex', justifyContent:'center', marginBottom:20, '--nxa':'ghFadeUp 0.6s var(--ease-out) both' }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:10 }}><NextEpLogo size={30} glow={false} /><Wordmark size={30} /></span>
        </div>
        {fromTvTime ? (
          <>
            {/* Rescue framing: pre-shutdown the job was "get your history out before
                July 15"; post-shutdown (tvtimeGone) it's "bring your saved export home"
, the gdpr.tvtime.com export portal is dead now, so never send them there. */}
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'color-mix(in srgb, var(--state-bad) 12%, transparent)', border:'1px solid color-mix(in srgb, var(--state-bad) 40%, transparent)', borderRadius:'var(--radius-xl)', padding:'5px 14px', marginBottom:16, '--nxa':'ghFadeUp 0.6s 0.08s var(--ease-out) both' }}>
              <span style={{ width:6, height:6, borderRadius:'var(--radius-full)', background:'var(--state-bad)', '--nxa':'heroPulse 2s ease-in-out infinite' }}/>
              <span style={{ color:'var(--state-bad)', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{tvtimeGone() ? 'TV Time has shut down' : 'TV Time deletes your history July 15'}</span>
            </div>
            <h1 style={{
              color:'var(--text-strong)', fontSize:30, fontWeight:600, lineHeight:1.14,
              margin:'0 0 10px', letterSpacing:'-0.5px', '--nxa':'ghFadeUp 0.6s 0.14s var(--ease-out) both',
            }}>
              {tvtimeGone()
                ? <>Bring your shows<br/><span style={{ color:'var(--amber)' }}>home</span>.</>
                : <>Get your shows<br/><span style={{ color:'var(--amber)' }}>out in time</span>.</>}
            </h1>
            <div style={{ color:'var(--text-muted)', fontSize:15, lineHeight:1.6, maxWidth:320, margin:'0 auto 16px', '--nxa':'ghFadeUp 0.6s 0.2s var(--ease-out) both' }}>
              {/* "…and the comments" earns its place here because it's the promise the
                  visitor arrived on. The Discord post and nextep.show/tv-time-alternative
                  both lead with "your comments come back" — and this page, the very next
                  thing they see, used to talk only about history and imports. A walk of
                  that exact path (2026-08-01) found the payoff missing at the handoff. */}
              {tvtimeGone()
                ? <>TV Time is gone, but if you saved your export, NextEp is ready and waiting. Pull it in with one tap — your whole history <b style={{ color:'var(--text-muted)' }}>and the comments you wrote</b>, which land back on the episodes you wrote them about. Trakt, Letterboxd, Simkl and IMDb come over too.</>
                : <>Your watch history disappears with TV Time on the 15th. NextEp is ready and waiting, pull it in with one tap, along with your lists from Trakt, Letterboxd, Simkl and IMDb. One home for everything you've tracked, before it's gone.</>}
            </div>
            <div style={{ color:'var(--text-dim)', fontSize:12, lineHeight:1.55, maxWidth:320, margin:'0 auto 22px', padding:'9px 12px', background:'rgba(var(--amber-rgb),0.07)', border:'1px solid rgba(var(--amber-rgb),0.2)', borderRadius:'var(--radius-md)', '--nxa':'ghFadeUp 0.6s 0.24s var(--ease-out) both' }}>
              {tvtimeGone()
                ? <><b style={{ color:'var(--amber)' }}>Have your export ready:</b> if you saved your TV Time <b style={{ color:'var(--text-muted)' }}>.zip</b> before the shutdown, it imports in one tap. No export in time? Recover from screenshots, or bring your list from Trakt, Letterboxd, Simkl or IMDb.</>
                : <><b style={{ color:'var(--amber)' }}>Start now:</b> request your export at <b style={{ color:'var(--text-muted)' }}>gdpr.tvtime.com</b>, it can take a little while to arrive, so kick it off before you sign in.</>}
            </div>
          </>
        ) : (
          <>
            {/* Scarcity framing (Jason 2026-06-10): access reads as limited-wave,
                not "beta", FOMO drives desire. Signup itself stays open. */}
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(var(--amber-rgb),0.1)', border:'1px solid rgba(var(--amber-rgb),0.3)', borderRadius:'var(--radius-xl)', padding:'5px 14px', marginBottom:16, '--nxa':'ghFadeUp 0.6s 0.08s var(--ease-out) both' }}>
              <span style={{ width:6, height:6, borderRadius:'var(--radius-full)', background:'var(--green)', '--nxa':'heroPulse 2s ease-in-out infinite' }}/>
              <span style={{ color:'var(--amber)', fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>Version 17 · free · no ads</span>
            </div>
            <h1 style={{
              color:'var(--text-strong)', fontSize:30, fontWeight:600, lineHeight:1.14,
              margin:'0 0 10px', letterSpacing:'-0.5px', '--nxa':'ghFadeUp 0.6s 0.14s var(--ease-out) both',
            }}>
              Never miss<br/>an <span style={{ color:'var(--amber)' }}>episode</span>.
            </h1>
            <div style={{ color:'var(--text-muted)', fontSize:15, lineHeight:1.6, maxWidth:300, margin:'0 auto 24px', '--nxa':'ghFadeUp 0.6s 0.2s var(--ease-out) both' }}>
              The fastest way to track TV. One card, one swipe, and you always know what to watch next.
            </div>
          </>
        )}
        <div style={{ '--nxa':'ghFadeUp 0.6s 0.26s var(--ease-out) both', display:'flex', flexDirection:'column', alignItems:'center', gap:14 }}>
          {/* CTA order, 2026-09-28: the "Claim your seat" scarcity framing (Jason
              2026-06-10, "FOMO drives desire") predates the guest-first pivot this app
              has since made (17.031-035: no account needed, a full guest library IS an
              app user). It was still the PRIMARY button, with the actual low-friction
              path buried under "Not ready to sign up?" — a real visitor walk confirmed
              it: tapping the amber button lands on a required-email form, the exact
              surface where an email scanner can silently burn a magic-link token before
              the person ever sees it (Sep 2026 read: 4-8 of that month's signups
              auto-authenticated in under 0.3s and never used the app). Flipped for the
              default case: the account-free path — the one nothing else in the product
              treats as a fallback — is now the amber button. The TV Time rescue variant
              below keeps email first on purpose: a large import benefits from a durable,
              cross-device account, and that visitor already came here to migrate. */}
          {fromTvTime ? (
            <button onClick={scrollToSignIn} className="ui-primary" style={{
              background:'var(--grad-amber)', border:'none', borderRadius:'var(--radius-md)',
              color:'var(--amber-ink)', fontWeight:600, fontSize:15, padding:'14px 30px', cursor:'pointer',
              fontFamily:'var(--font)', boxShadow:'0 14px 40px -12px color-mix(in srgb, var(--amber) 80%, transparent)',
            }}>
              Rescue my history →
            </button>
          ) : (
            <button onClick={() => navigate('/start')} className="ui-primary" style={{
              background:'var(--grad-amber)', border:'none', borderRadius:'var(--radius-md)',
              color:'var(--amber-ink)', fontWeight:600, fontSize:15, padding:'14px 30px', cursor:'pointer',
              fontFamily:'var(--font)', boxShadow:'0 14px 40px -12px color-mix(in srgb, var(--amber) 80%, transparent)',
            }}>
              📺 Track your shows now →
            </button>
          )}
          <div style={{ color:'var(--text-dim)', fontSize:12, fontWeight:600, letterSpacing:'0.02em' }}>
            {fromTvTime ? 'Not ready to sign up?' : 'Want it on every device?'}
          </div>
          <Button variant="secondary" onClick={fromTvTime ? () => navigate('/start') : scrollToSignIn} style={{ fontSize:14.5 }}>
            {fromTvTime ? '📺 Track your shows now' : 'Save your list with an email'}
          </Button>
        </div>
        {/* Social proof stats */}
        <div style={{ display:'flex', justifyContent:'center', gap:24, marginTop:26, '--nxa':'ghFadeUp 0.6s 0.32s var(--ease-out) both' }}>
          {[
            { v:'1-tap', l:'TV Time import' },
            { v:'TV only', l:'no movie clutter' },
            { v:'1 swipe', l:'marks an episode' },
          ].map(s => (
            <div key={s.l} style={{ textAlign:'center' }}>
              <div style={{ color:'var(--amber)', fontWeight:600, fontSize:17 }}>{s.v}</div>
              <div style={{ color:'var(--text-dim)', fontSize:10 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Post-hero content — capped to a centered editorial column on desktop
          (.gh-body), full-width phone layout untouched below 720px. */}
      <div className='gh-body'>

      {/* ── LIVE PEEK, real trending shows (public TMDB), proof over mockups ── */}
      {peek.length > 0 && (
        <div style={{ padding:'4px 0 6px', '--nxa':'ghFadeUp 0.6s 0.36s var(--ease-out) both' }}>
          <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', padding:'0 16px 10px' }}>
            <div style={{ color:'var(--text-strong)', fontSize:13, fontWeight:700 }}>Trending this week</div>
            <button onClick={() => navigate('/discover')} style={{ background:'none', border:'none', color:'var(--amber)', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'var(--font)', padding:'12px 0 12px 12px', margin:'-12px 0' }}>Browse all →</button>
          </div>
          {/* data-visual-volatile: real TMDB trending, so it changes week to week.
              The visual-regression suite masks it — otherwise every rotation of the
              trending set reads as a UI regression (e2e/visual.spec.js). */}
          <div data-visual-volatile style={{ display:'flex', gap:10, overflowX:'auto', scrollbarWidth:'none', padding:'0 16px 4px', WebkitOverflowScrolling:'touch' }}>
            {peek.map(s => (
              <button key={s.id} onClick={() => navigate('/discover')} style={{ flexShrink:0, width:96, background:'none', border:'none', padding:0, cursor:'pointer', textAlign:'left' }}>
                <img src={`https://image.tmdb.org/t/p/w185${s.poster_path}`} alt={s.name || ''} loading="lazy" decoding="async"
                  style={{ width:96, aspectRatio:'2/3', objectFit:'cover', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', display:'block', boxShadow:'0 6px 18px rgba(0,0,0,0.4)' }}/>
                <div style={{ color:'var(--text-dim)', fontSize:10.5, fontWeight:500, marginTop:5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</div>
              </button>
            ))}
          </div>
          <div style={{ color:'var(--text-dim)', fontSize:11, textAlign:'center', padding:'8px 16px 0' }}>Browse the full catalog free. No account needed to start tracking.</div>
        </div>
      )}

      {/* ── SIGNATURE FEATURES, real mini mockups, not just emoji rows ───── */}
      <div style={{ padding:'14px 16px 4px' }}>
        <div style={{ color:'var(--text-dim)', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.14em', textAlign:'center', marginBottom:14 }}>
          Built different
        </div>

        <div className='gh-features'>
        {/* 1, Mark watched: mini clapboard + reel-knob toggle */}
        <div style={{ display:'flex', alignItems:'center', gap:16, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:18, padding:'16px 18px', marginBottom:12, '--nxa':'ghSlideUp 0.5s 0.1s var(--ease-soft) both' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:'var(--text-strong)', fontWeight:600, fontSize:14.5, marginBottom:4 }}>Marking watched feels like slating a take</div>
            <div style={{ color:'var(--text-dim)', fontSize:12, lineHeight:1.5 }}>A real clapboard swing, a gold flash, always an undo. Not a checkbox.</div>
          </div>
          <div style={{ flexShrink:0, width:52, height:52, borderRadius:'var(--radius-md)', background:'rgba(var(--amber-rgb),0.08)', border:'1px solid rgba(var(--amber-rgb),0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>🎬</div>
        </div>

        {/* 2, Rate: five reels (the race card is cut in 17) */}
        <div style={{ display:'flex', alignItems:'center', gap:16, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:18, padding:'16px 18px', marginBottom:12, '--nxa':'ghSlideUp 0.5s 0.18s var(--ease-out) both' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:'var(--text-strong)', fontWeight:600, fontSize:14.5, marginBottom:4 }}>Tap to rate. Five reels, not five stars.</div>
            <div style={{ color:'var(--text-dim)', fontSize:12, lineHeight:1.5 }}>Mark it watched and the reels slide up. Binge Alert, All-Timer, you know the scale.</div>
          </div>
          <div style={{ flexShrink:0, display:'flex', gap:3 }}>{[1,2,3,4].map(n => <FilmReel key={n} size={14} color="var(--amber)" />)}<FilmReel size={14} color="rgba(var(--ink-rgb),0.3)" /></div>
        </div>

        {/* 3, Every finish gets one good moment */}
        <div style={{ display:'flex', alignItems:'center', gap:16, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:18, padding:'16px 18px', marginBottom:4, '--nxa':'ghSlideUp 0.5s 0.26s var(--ease-soft) both' }}>
          <div style={{ flexShrink:0, width:52, height:52, borderRadius:'var(--radius-md)', background:'rgba(var(--amber-rgb),0.08)', border:'1px solid rgba(var(--amber-rgb),0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}><ClapboardClosed size={24} color="var(--amber)" /></div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ color:'var(--text-strong)', fontWeight:600, fontSize:14.5, marginBottom:4 }}>Every finish gets one good moment</div>
            <div style={{ color:'var(--text-dim)', fontSize:12, lineHeight:1.5 }}>Season done, series wrapped. The show’s own colour, one line, and a rating. No confetti.</div>
          </div>
        </div>
        </div>{/* /gh-features */}
      </div>

      {/* ── SIGN-IN, the primary conversion surface, deliberately elevated ─── */}
      <div ref={signInRef} className='gh-seat' style={{
        position:'relative', margin:'22px 16px 8px',
        background:'linear-gradient(160deg, rgba(var(--amber-rgb),0.13), var(--bg-card) 58%)',
        border:'1.5px solid rgba(var(--amber-rgb),0.55)', borderRadius:'var(--radius-xl)', padding:'22px 18px',
        boxShadow:'0 0 40px rgba(var(--amber-rgb),0.14), 0 12px 36px rgba(0,0,0,0.5)',
      }}>
        {waitlisted ? (
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ fontSize:40, marginBottom:10 }}>🎟️</div>
            <div style={{ color:'var(--text-strong)', fontWeight:700, fontSize:16, marginBottom:6 }}>You're on the list</div>
            <div style={{ color:'var(--text-dim)', fontSize:13, lineHeight:1.6 }}>
              A wave just filled up, so we saved a spot for <span style={{ color:'var(--amber)' }}>{email}</span> and we'll email your invite the moment the next one opens.
              {fromTvTime && <> In the meantime, have your <b style={{ color:'var(--text-muted)' }}>saved TV Time export</b> (.zip) ready so it imports the moment your invite lands.</>}
            </div>
            <button onClick={() => { setWaitlisted(false); setEmail('') }} style={{
              marginTop:14, background:'none', border:'1px solid var(--border)',
              borderRadius:'var(--radius-xl)', padding:'7px 18px', color:'var(--text-dim)',
              fontSize:12, cursor:'pointer', fontFamily:'var(--font)',
            }}>
              Use a different email
            </button>
          </div>
        ) : sent ? (
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ fontSize:40, marginBottom:10 }}>✉️</div>
            <div style={{ color:'var(--text-strong)', fontWeight:700, fontSize:16, marginBottom:6 }}>Check your email</div>
            <div style={{ color:'var(--text-dim)', fontSize:13, lineHeight:1.6 }}>
              We sent a code to <span style={{ color:'var(--amber)' }}>{email}</span>. Enter it below — or tap the link in the email on this device.
            </div>

            {/* Code entry is the PRIMARY path, not a fallback: the emailed link is
                routinely consumed by mail-provider scanners before the human opens
                the message (see verifyCode). inputMode/autoComplete give iOS and
                Android the one-tap "paste from Messages/Mail" affordance. */}
            <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
              <input
                value={code}
                onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 10)); setErr('') }}
                onKeyDown={e => e.key === 'Enter' && verifyCode()}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                aria-label="Sign-in code"
                style={{
                  width:196, textAlign:'center', background:'var(--surface)',
                  border:'1px solid var(--border)', borderRadius:'var(--radius)',
                  padding:'0 12px', height:44, color:'var(--text-strong)',
                  fontSize:20, fontWeight:700, letterSpacing:'0.28em',
                  fontFamily:'var(--font-mono, monospace)',
                }}
              />
              <button
                onClick={verifyCode}
                disabled={code.length < 6 || codeLoading}
                className="ui-primary"
                style={{
                  background:'var(--grad-amber)', border:'none', borderRadius:'var(--radius)',
                  padding:'0 18px', height:44, color:'var(--amber-ink)', fontWeight:600, fontSize:14,
                  fontFamily:'var(--font)', cursor: code.length < 6 ? 'default' : 'pointer',
                  opacity: code.length < 6 ? 0.45 : 1,
                }}
              >
                {codeLoading ? <Spinner size={15} color="#000" track="rgba(0,0,0,0.25)"/> : 'Sign in'}
              </button>
            </div>
            {err && <div style={{ color:'var(--danger-text)', fontSize:12, marginTop:10 }}>{err}</div>}

            <div style={{ color:'var(--text-dim)', fontSize:11.5, lineHeight:1.55, marginTop:12, opacity:0.8 }}>
              Can take a minute to arrive. No email? Check spam, or <a href='/login' style={{ color:'var(--amber)', textDecoration:'none' }}>sign in with a password</a>.
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:14 }}>
              <button onClick={() => { if (resendIn <= 0 && !loading) sendMagicLink() }} disabled={resendIn > 0 || loading} style={{
                background: resendIn > 0 ? 'none' : 'rgba(var(--amber-rgb),0.12)',
                border:`1px solid ${resendIn > 0 ? 'var(--border)' : 'rgba(var(--amber-rgb),0.3)'}`,
                borderRadius:'var(--radius-xl)', padding:'7px 18px',
                color: resendIn > 0 ? 'var(--text-dim)' : 'var(--amber)',
                fontSize:12, fontWeight:600, cursor: resendIn > 0 ? 'default' : 'pointer', fontFamily:'var(--font)',
              }}>
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
              <button onClick={() => { setSent(false); setEmail(''); setCode('') }} style={{
                background:'none', border:'1px solid var(--border)',
                borderRadius:'var(--radius-xl)', padding:'7px 18px', color:'var(--text-dim)',
                fontSize:12, cursor:'pointer', fontFamily:'var(--font)',
              }}>
                Different email
              </button>
            </div>
          </div>
        ) : (
          <>
            {linkErr && (
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, background:'rgba(var(--amber-rgb),0.1)', border:'1px solid rgba(var(--amber-rgb),0.28)', borderRadius:'var(--radius)', padding:'10px 12px', color:'var(--text-muted)', fontSize:12.5, lineHeight:1.5 }}>
                {linkErr}
              </div>
            )}
            <div style={{ textAlign:'center', fontFamily:'var(--font-mono)', color:'var(--amber)', fontSize:10.5, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:8 }}>
              Sign in or start
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
              <Sparkles size={15} style={{ color:'var(--amber)', flexShrink:0 }}/>
              <div style={{ color:'var(--text-strong)', fontWeight:700, fontSize:15 }}>
                Start with your email
              </div>
            </div>
            <div style={{ display:'flex', gap:8, marginBottom: err ? 10 : 0 }}>
              <div style={{ flex:1, display:'flex', alignItems:'center', gap:8, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'0 12px', height:44 }}>
                <Mail size={15} style={{ color:'var(--text-dim)', flexShrink:0 }}/>
                <input
                  type="email"
                  placeholder="your@email.com"
                  aria-label="Email address"
                  autoComplete="email"
                  autoFocus={!skipAutoFocus}
                  value={email}
                  onChange={e => { setEmail(e.target.value); setErr('') }}
                  onKeyDown={e => e.key === 'Enter' && sendMagicLink()}
                  style={{
                    flex:1, background:'none', border:'none', color:'var(--text-strong)', fontSize:16, fontFamily:'var(--font)',
                  }}
                />
              </div>
              <button
                onClick={sendMagicLink}
                disabled={loading || !email.trim()}
                aria-label="Send sign-in link"
                style={{
                  background:'var(--grad-amber)', border:'none', borderRadius:'var(--radius)',
                  width:46, height:44, display:'flex', alignItems:'center',
                  justifyContent:'center', cursor: email.trim() ? 'pointer' : 'default',
                  opacity: email.trim() ? 1 : 0.45, flexShrink:0,
                  boxShadow: email.trim() ? '0 0 18px rgba(var(--amber-rgb),0.55)' : 'none',
                  transition:'opacity 0.15s, box-shadow 0.2s',
                }}
              >
                {loading
                  ? <Spinner size={16} color="#000" track="rgba(0,0,0,0.25)"/>
                  : <ChevronRight size={18} style={{ color:'#000' }}/>
                }
              </button>
            </div>
            {err && <div style={{ color:'var(--danger-text)', fontSize:12, marginTop:4 }}>{err}</div>}
            {/* A mistyped domain is a silent total loss — the link goes nowhere and the
                account is unreachable forever (3 real accounts died this way). Suggest,
                never block: the address they typed always stays valid. */}
            {emailFix && (
              <button
                onClick={() => { setEmail(emailFix); setErr('') }}
                style={{
                  background:'none', border:'none', padding:'6px 0 0', cursor:'pointer',
                  fontFamily:'var(--font)', fontSize:12, textAlign:'left', color:'var(--text-dim)',
                }}
              >
                Did you mean <span style={{ color:'var(--amber)', fontWeight:700 }}>{emailFix}</span>?
              </button>
            )}
            <div style={{ color:'var(--text-dim)', fontSize:11, textAlign:'center', marginTop:10 }}>
              New here? Same box, an account is created automatically.
            </div>
            {/* Password/Google/passkey path, ESSENTIAL in the installed PWA: a magic
                link opens in Safari, whose session never reaches the PWA's own
                storage. The other methods happen fully in-app. In standalone we
                PROMOTE it to a prominent button so installed users don't dead-end
                on the magic link above; in-browser it stays a quiet footer link.
                Label says "another way", not "a password" — /login also offers
                Google and passkey, and the old copy undersold that (user report,
                2026-08-17: screenshot showed no indication either existed until
                after tapping through). */}
            {isStandalone ? (
              <div style={{ marginTop:14 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, margin:'0 0 12px' }}>
                  <div style={{ flex:1, height:1, background:'var(--border)' }}/>
                  <span style={{ color:'var(--text-dim)', fontSize:11 }}>installed the app?</span>
                  <div style={{ flex:1, height:1, background:'var(--border)' }}/>
                </div>
                <button onClick={() => navigate('/login')} style={{
                  width:'100%', background:'var(--surface)', border:'1px solid rgba(var(--amber-rgb),0.35)',
                  borderRadius:'var(--radius)', color:'var(--amber)', fontWeight:700, fontSize:14,
                  padding:'12px', cursor:'pointer', fontFamily:'var(--font)',
                }}>
                  Sign in another way →
                </button>
                <div style={{ color:'var(--text-dim)', fontSize:11, textAlign:'center', marginTop:8, lineHeight:1.5 }}>
                  Google, passkey, or password — email links open your browser, not this app.
                </div>
              </div>
            ) : (
              <div style={{ textAlign:'center', marginTop:10 }}>
                <a href='/login' style={{ color:'var(--amber)', fontSize:13, fontWeight:600, textDecoration:'none', borderBottom:'1px dotted rgba(var(--amber-rgb),0.5)', paddingBottom:1 }}>
                  Or sign in with Google, a passkey, or a password →
                </a>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── ADD TO HOME SCREEN, capture the install on the LOGGED-OUT surface,
             where most launch traffic (phones) lands. The authed InstallBanner only
             fires after sign-in + 3 shows, so a first-time visitor never got asked. ── */}
      <GuestInstallHint />

      {/* ── COMPACT FEATURE LIST ──────────────────────────────────────────── */}
      <div style={{ margin:'0 16px', background:'var(--bg-card)', border:'1px solid var(--border-soft)', borderRadius:18, padding:'16px 16px 8px' }}>
        <div style={{ color:'var(--text-dim)', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:12 }}>
          What's inside
        </div>
        {FEATURES.map((f, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderTop: i > 0 ? '1px solid var(--border-soft)' : 'none' }}>
            <span style={{ fontSize:20, width:28, textAlign:'center', flexShrink:0 }}>{f.icon}</span>
            <span style={{ color:'var(--text-muted)', fontSize:13, lineHeight:1.4 }}>{f.text}</span>
          </div>
        ))}
      </div>

      <div style={{ textAlign:'center', padding:'20px 0 40px', color:'var(--text-dim)', fontSize:11, lineHeight:1.7 }}>
        <div style={{ color:'var(--text-muted)', fontWeight:600 }}>Your data is yours, export it to JSON or CSV anytime.</div>
        <div style={{ marginTop:6 }}>
          <a href="/privacy" style={{ color:'var(--text-dim)', textDecoration:'underline', textUnderlineOffset:2 }}>Privacy</a>
          {' · '}
          <a href="/terms" style={{ color:'var(--text-dim)', textDecoration:'underline', textUnderlineOffset:2 }}>Terms</a>
        </div>
        <div style={{ marginTop:6 }}>Free, no ads · Made for people who love TV</div>
      </div>

      </div>{/* /gh-body */}
      <style>{`

        @keyframes ghFadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes ghSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        /* Gentle breathing glow so the sign-up card draws the eye without shouting. */
        @keyframes ghSeatGlow {
          0%,100% { box-shadow:0 0 34px rgba(var(--amber-rgb),0.12), 0 12px 36px rgba(0,0,0,0.5) }
          50%     { box-shadow:0 0 54px rgba(var(--amber-rgb),0.22), 0 12px 36px rgba(0,0,0,0.5) }
        }
        .gh-seat { animation: ghSeatGlow 3.8s ease-in-out infinite; }
        @keyframes ghBulbGlow { 0%,100% { opacity:1 } 50% { opacity:0.35 } }
        .gh-bulb { animation: ghBulbGlow 1.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .gh-seat, .gh-bulb { animation: none; } }
      `}</style>

    </div>
  )
}

// ── GuestInstallHint ─────────────────────────────────────────────────────────
// A compact "add to home screen" affordance for the logged-out landing. NextEp is
// PWA-only (no App Store), so home-screen install is the whole re-engagement path
// (push, standalone session, the auth cookie bridge). Android/Chrome: capture
// beforeinstallprompt and offer a one-tap Install. iOS Safari (no such event):
// show the Share → Add to Home Screen instructions. Hidden once installed
// (standalone) or dismissed. Kept dependency-free to stay light on this cold load.
function GuestInstallHint() {
  const [deferred, setDeferred] = useState(null)
  const [isIOS,    setIsIOS]    = useState(false)
  const [show,     setShow]     = useState(false)
  useEffect(() => {
    try {
      if (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator?.standalone) return
      if (safeStorage.get('guest_install_dismissed') === '1') return
    } catch { return }
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream
    setIsIOS(ios)
    if (ios) { setShow(true); return }
    // Android/Chrome fire beforeinstallprompt ONCE, early; main.jsx captures it as
    // window.__nextepInstallEvt so a late listener here doesn't miss it (2026-10-15).
    if (window.__nextepInstallEvt) { setDeferred(window.__nextepInstallEvt); setShow(true) }
    const handler = () => { if (window.__nextepInstallEvt) { setDeferred(window.__nextepInstallEvt); setShow(true) } }
    window.addEventListener('nextep:installable', handler)
    return () => window.removeEventListener('nextep:installable', handler)
  }, [])
  const dismiss = () => { try { safeStorage.set('guest_install_dismissed', '1') } catch {} ; setShow(false) }
  const install = async () => {
    if (!deferred) return
    deferred.prompt()
    try { const { outcome } = await deferred.userChoice; if (outcome === 'accepted') dismiss() } catch {}
  }
  if (!show) return null
  return (
    <div style={{
      margin:'12px 16px 0', background:'var(--bg-card)', border:'1px solid rgba(var(--amber-rgb),0.25)',
      borderRadius:18, padding:'12px 14px', display:'flex', alignItems:'center', gap:12, fontFamily:'var(--font)',
    }}>
      <div style={{ fontSize:26, flexShrink:0 }} aria-hidden="true">📲</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ color:'var(--text-strong)', fontWeight:700, fontSize:13 }}>Add NextEp to your home screen</div>
        <div style={{ color:'var(--text-dim)', fontSize:11, marginTop:2, lineHeight:1.45 }}>
          {isIOS ? 'In Safari: tap ··· at the bottom, then Share, then View More, then “Add to Home Screen”. It opens like a real app.' : 'Install it like an app, faster, full-screen, and reminders when episodes drop.'}
        </div>
      </div>
      {!isIOS && (
        <button onClick={install} style={{
          background:'rgba(var(--amber-rgb),0.15)', border:'1px solid rgba(var(--amber-rgb),0.4)',
          borderRadius:'var(--radius-sm)', padding:'7px 12px', color:'var(--amber)', fontWeight:700, fontSize:12,
          cursor:'pointer', flexShrink:0, fontFamily:'var(--font)', display:'flex', alignItems:'center', gap:5,
        }}>
          <Download size={12}/> Install
        </button>
      )}
      <button className="ui-hit" onClick={dismiss} aria-label="Dismiss" style={{
        background:'var(--surface)', border:'none', borderRadius:'var(--radius-full)', width:26, height:26,
        color:'var(--text-dim)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
      }}>
        <X size={12}/>
      </button>
    </div>
  )
}
